import React from "react";
import { useTranslation } from "react-i18next";
import { AudienceData } from "../../../domain";

// ── Helpers ───────────────────────────────────────────────────────────────────

function sortedTop(data: Record<string, number>, n = 10): [string, number][] {
  return Object.entries(data)
    .sort((a, b) => b[1] - a[1])
    .slice(0, n);
}

// ── BarList ───────────────────────────────────────────────────────────────────

interface BarListProps { data: Record<string, number>; label: string }

const BarList: React.FC<BarListProps> = ({ data, label }) => {
  const { i18n } = useTranslation();
  const locale = i18n.language.startsWith("en") ? "en-US" : "ru-RU";
  const entries = sortedTop(data);
  if (entries.length === 0) return null;
  const max = entries[0][1];
  return (
    <div>
      <div className="aud-sub-title">{label}</div>
      <div className="bar-list">
        {entries.map(([key, val]) => (
          <div key={key} className="bar-item">
            <span className="bar-name">{key}</span>
            <div className="bar-track">
              <div className="bar-fill" style={{ width: `${Math.round((val / max) * 100)}%` }} />
            </div>
            <span className="bar-num">{val.toLocaleString(locale)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── GenderAgeChart ────────────────────────────────────────────────────────────

interface GenderAgeChartProps { data: Record<string, number> }

const GenderAgeChart: React.FC<GenderAgeChartProps> = ({ data }) => {
  const { t } = useTranslation();

  const ageGroups = Array.from(
    new Set(Object.keys(data).map(k => k.split(".")[1]))
  ).sort();

  const total = Object.values(data).reduce((s, v) => s + v, 0) || 1;
  const getVal = (gender: string, age: string) => data[`${gender}.${age}`] ?? 0;

  const gaMax = Math.max(...ageGroups.flatMap(age => [getVal("F", age), getVal("M", age)])) || 1;

  return (
    <div style={{ marginTop: 36 }}>
      <div className="aud-sub-title">{t("report.genderAge")}</div>
      <div className="ga-chart" style={{ maxWidth: 580, marginTop: 16 }}>
        {ageGroups.map(age => {
          const f = getVal("F", age);
          const m = getVal("M", age);
          const fw = ((f / gaMax) * 100).toFixed(1);
          const mw = ((m / gaMax) * 100).toFixed(1);
          const fPct = Math.round((f / total) * 100);
          const mPct = Math.round((m / total) * 100);
          return (
            <div key={age} className="ga-row">
              <span className="ga-age">{age}</span>
              <div className="ga-bars">
                <div className="ga-bar-wrap">
                  <div className="ga-bar f" style={{ width: `${fw}%` }}>
                    {fPct >= 2 ? `${fPct}%` : ""}
                  </div>
                </div>
                <div className="ga-bar-wrap">
                  <div className="ga-bar m" style={{ width: `${mw}%` }}>
                    {mPct >= 2 ? `${mPct}%` : ""}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        <div className="ga-legend">
          <div className="ga-legend-item">
            <div className="ga-swatch" style={{ background: "oklch(0.62 0.14 350)" }} />
            {t("report.female")}
          </div>
          <div className="ga-legend-item">
            <div className="ga-swatch" style={{ background: "oklch(0.44 0.06 220)" }} />
            {t("report.male")}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── AudienceSection ───────────────────────────────────────────────────────────

interface AudienceSectionProps { audience?: AudienceData }

export const AudienceSection: React.FC<AudienceSectionProps> = ({ audience }) => {
  const { t } = useTranslation();
  const hasData = audience && (
    (audience.cities    && Object.keys(audience.cities).length > 0) ||
    (audience.countries && Object.keys(audience.countries).length > 0) ||
    (audience.gender_age && Object.keys(audience.gender_age).length > 0)
  );

  return (
    <div className="rp-sec rp-sec-tinted">
      <div className="sec-header">
        <span className="sec-eyebrow">Аудитория</span>
        <h2 className="sec-title">{t("report.audience")}</h2>
      </div>
      {hasData ? (
        <>
          <div className="aud-grid">
            {audience!.countries && Object.keys(audience!.countries).length > 0 && (
              <BarList data={audience!.countries} label={t("report.countries")} />
            )}
            {audience!.cities && Object.keys(audience!.cities).length > 0 && (
              <BarList data={audience!.cities} label={t("report.cities")} />
            )}
          </div>
          {audience!.gender_age && Object.keys(audience!.gender_age).length > 0 && (
            <GenderAgeChart data={audience!.gender_age} />
          )}
        </>
      ) : (
        <div className="aud-placeholder">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <circle cx="12" cy="8" r="3.5"/><path d="M3 21a9 9 0 0 1 18 0"/><path d="M12 14v7M9 17h6"/>
          </svg>
          <p>
            {t("report.audienceUnavailable")}
            <br />
            <span style={{ color: "var(--ink-pale)", fontSize: "0.85em" }}>
              {t("report.audienceUnavailableNote").split("instagram_manage_insights").map((part: string, i: number) =>
                i === 0
                  ? <React.Fragment key={i}>{part}<code style={{ fontFamily: "var(--font-mono)", fontSize: "0.78rem", background: "var(--bg-warm)", color: "var(--gold-ink, var(--gold))", padding: "2px 7px", borderRadius: 5 }}>instagram_manage_insights</code></React.Fragment>
                  : <React.Fragment key={i}>{part}</React.Fragment>
              )}
            </span>
          </p>
        </div>
      )}
    </div>
  );
};
